import { useEffect, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { IDEA_LIMITS } from '@twinden/shared';
import { checkIdea } from '../../lib/ideas';
import { useT } from '../../i18n';

// Posting or editing an idea on Shape Twinden (M55, D198): a title and
// optional details, plain text, no attachments. A bottom sheet on phones, a
// dialog wider up, like the greeting sheet.

export interface IdeaDraft {
  title: string;
  body: string;
}

function Counter({ length, max }: { length: number; max: number }) {
  return (
    <Text
      fontSize="13px"
      color={length > max ? 'danger' : 'muted'}
      aria-hidden="true"
    >
      {length}/{max}
    </Text>
  );
}

export function IdeaSheet({
  isOpen,
  onClose,
  initial,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Editing: the idea as it is. */
  initial?: IdeaDraft | null;
  /** Resolves when saved; throws to keep the sheet open. */
  onSubmit: (draft: IdeaDraft) => Promise<void>;
}) {
  const t = useT();
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(initial?.title ?? '');
    setBody(initial?.body ?? '');
    setTried(false);
  }, [isOpen, initial]);

  const check = checkIdea(title, body);
  // "Give it a title" waits for a try; too long shows as you type.
  const titleProblem =
    check.title && (tried || check.title.key === 'site.ideas.sheet.tooLong')
      ? check.title
      : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!check.ok) return;
    setSaving(true);
    try {
      await onSubmit({ title: title.trim(), body: body.trim() });
    } catch {
      // The caller showed why; keep what was typed.
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <VStack align="stretch" spacing={4}>
        <Heading as="h2" fontSize="22px">
          {initial
            ? t('site.ideas.sheet.editTitle')
            : t('site.ideas.sheet.title')}
        </Heading>
        <FormControl isInvalid={!!titleProblem}>
          <HStack justify="space-between" align="baseline">
            <FormLabel mb={1}>{t('site.ideas.sheet.titleLabel')}</FormLabel>
            <Counter length={title.trim().length} max={IDEA_LIMITS.TITLE_MAX} />
          </HStack>
          <Input
            value={title}
            autoFocus
            placeholder={t('site.ideas.sheet.titlePlaceholder')}
            onChange={(event) => setTitle(event.target.value)}
          />
          {titleProblem && (
            <FormErrorMessage>
              {t(titleProblem.key, titleProblem.vars)}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl isInvalid={!!check.body}>
          <HStack justify="space-between" align="baseline">
            <FormLabel mb={1}>{t('site.ideas.sheet.bodyLabel')}</FormLabel>
            <Counter length={body.trim().length} max={IDEA_LIMITS.BODY_MAX} />
          </HStack>
          <Textarea
            value={body}
            rows={5}
            placeholder={t('site.ideas.sheet.bodyPlaceholder')}
            onChange={(event) => setBody(event.target.value)}
          />
          {check.body && (
            <FormErrorMessage>
              {t(check.body.key, check.body.vars)}
            </FormErrorMessage>
          )}
        </FormControl>
        <Text fontSize="14px" color="muted">
          {t('site.ideas.sheet.anonymous')}
        </Text>
        <HStack justify="flex-end" spacing={2}>
          <Button variant="ghost" colorScheme="gray" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            isLoading={saving}
            isDisabled={tried && !check.ok}
          >
            {initial ? t('site.ideas.sheet.save') : t('site.ideas.sheet.post')}
          </Button>
        </HStack>
      </VStack>
    </form>
  );

  if (phone) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 16px)"
        >
          <DrawerBody px={5} pt="10px" pb={2} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="full"
              bg="line"
              mx="auto"
              mb={4}
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={5} borderRadius="28px" bg="surface">
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
